import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import NotificationBell from './NotificationBell';
import { useAuth } from '../../context/AuthContext';
import { useSubscription, ROUTE_FEATURE_MAP } from '../../context/SubscriptionContext';
import supabase from '../../services/supabaseClient';
import { validateUsername, checkUsernameAvailability, canChangeUsername, getUsernameHistory, changeUsername, USERNAME_COOLDOWN_DAYS } from '../../services/usernameService';
import SupportHistoryModal from '../support/SupportHistoryModal';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, logout, displayName, username, setUsername, careHomeId, isAdmin, isCareHomeManager, isSuperAdmin, isOrgAdmin } = useAuth();
  const { hasFeature, canAccessRoute } = useSubscription();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isSupportHistoryOpen, setIsSupportHistoryOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState('');
  const [changeSuccess, setChangeSuccess] = useState(false);
  const [successCountdown, setSuccessCountdown] = useState(0);
  const countdownRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [careHomeName, setCareHomeName] = useState(null);
  // Change Username state
  const [isChangeUsernameOpen, setIsChangeUsernameOpen] = useState(false);
  const [newUsernameInput, setNewUsernameInput] = useState('');
  const [usernameValidation, setUsernameValidation] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameCheckLoading, setUsernameCheckLoading] = useState(false);
  const [usernameChangeLoading, setUsernameChangeLoading] = useState(false);
  const [usernameChangeError, setUsernameChangeError] = useState('');
  const [usernameChangeSuccess, setUsernameChangeSuccess] = useState(false);
  const [usernameCooldown, setUsernameCooldown] = useState(null);
  const [usernameHistory, setUsernameHistory] = useState([]);
  const usernameCheckTimer = useRef(null);
  const userMenuRef = useRef(null);

  // Fetch care home name from database using UUID
  useEffect(() => {
    const fetchCareHomeName = async () => {
      console.log('Fetching care home name for ID:', careHomeId);
      const isStaffLike = String(role || '').toLowerCase().includes('staff');
      if ((isStaffLike || isCareHomeManager) && careHomeId) {
        try {
          const { data, error } = await supabase
            .from('care_homes')
            .select('name')
            .eq('id', careHomeId)
            .single();
          
          if (error) {
            console.error('Error fetching care home:', error);
            setCareHomeName(null);
          } else {
            console.log('Fetched care home data:', data);
            setCareHomeName(data?.name || null);
          }
        } catch (err) {
          console.error('Error fetching care home name:', err);
          setCareHomeName(null);
        }
      } else {
          setCareHomeName(null);
      }
    };

    fetchCareHomeName();
  }, [careHomeId, role]);

  const navigationItems = [
    { label: 'Dashboard', path: '/main-dashboard', icon: 'LayoutDashboard', roles: ['user', 'admin'] },
    { label: 'Calendar', path: '/food-calendar-view', icon: 'Calendar', roles: ['user', 'admin'] },
    { label: 'Meals', path: '/meals-management', icon: 'UtensilsCrossed', roles: ['user', 'admin'] },
    { label: 'Ingredients', path: '/ingredients-management', icon: 'Leaf', roles: ['user', 'admin'] },
    { label: 'Analytics', path: '/cost-analytics', icon: 'TrendingUp', roles: ['user', 'admin'] },
    { label: 'Community', path: '/community', icon: 'MessagesSquare', roles: ['user', 'admin'] },
    { label: 'Delivery Status', path: '/delivery-status', icon: 'ClipboardCheck', roles: ['user', 'admin'] },
    { label: 'Help', path: '/help', icon: 'HelpCircle', roles: ['user', 'admin'] },
    // { label: 'Compliance', path: '/compliance-reporting', icon: 'FileCheck', roles: ['user', 'admin'] },
  ].filter(item => canAccessRoute(item.path));

  const staffNavItems = [
    { label: 'Dashboard', path: '/staff/dashboard', icon: 'LayoutDashboard' },
    { label: 'Calendar', path: '/food-calendar-view', icon: 'Calendar' },
    { label: 'Meals', path: '/meals-management', icon: 'UtensilsCrossed' },
    { label: 'Community', path: '/community', icon: 'MessagesSquare' },
    { label: 'Delivery Status', path: '/delivery-status', icon: 'ClipboardCheck' },
    { label: 'Help', path: '/help', icon: 'HelpCircle' },
  ].filter(item => canAccessRoute(item.path));

  // Care Home Managers get the staff base + Ingredients, Analytics
  const careHomeManagerNavItems = [
    { label: 'Dashboard', path: '/staff/dashboard', icon: 'LayoutDashboard' },
    { label: 'Calendar', path: '/food-calendar-view', icon: 'Calendar' },
    { label: 'Meals', path: '/meals-management', icon: 'UtensilsCrossed' },
    { label: 'Ingredients', path: '/ingredients-management', icon: 'Leaf' },
    { label: 'Analytics', path: '/cost-analytics', icon: 'TrendingUp' },
    { label: 'Community', path: '/community', icon: 'MessagesSquare' },
    { label: 'Delivery Status', path: '/delivery-status', icon: 'ClipboardCheck' },
    { label: 'Help', path: '/help', icon: 'HelpCircle' },
  ].filter(item => canAccessRoute(item.path));

  // Care Home Manager admin-like items (Residents for their care home)
  const careHomeManagerAdminItems = [
    { label: 'Residents', path: '/admin/resident-management', icon: 'UserCheck' },
  ].filter(item => canAccessRoute(item.path));

  const isCareHomeManagerRole = role === 'care_home_manager' || role === 'Care Home Manager';
  const isStaffLikeRole = role === 'staff' || isCareHomeManagerRole;
  const isAdminLikeRole = isAdmin || isSuperAdmin || isOrgAdmin;
  const mobilePrimaryNavItems = isStaffLikeRole
    ? (isCareHomeManagerRole ? careHomeManagerNavItems : staffNavItems)
    : navigationItems;

  const adminNavItems = [
    { label: 'Users', path: '/admin/user-management', icon: 'Users' },
    { label: 'Care Homes', path: '/admin/care-home-management', icon: 'Building2' },
    { label: 'Residents', path: '/admin/resident-management', icon: 'UserCheck' },
  ].filter(item => canAccessRoute(item.path));

  // Super Admin specific navigation (only visible for Super Admin role)
  const superAdminNavItems = [
    { label: 'Admin Portal', path: '/super-admin', icon: 'Shield' },
    { label: 'Audit Logs', path: '/audit-logs', icon: 'FileText' },
  ].filter(item => canAccessRoute(item.path));
  
  // NOTE: useAuth provides boolean helpers; keep compatibility for role strings as fallback

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const simulateConnection = () => {
      const statuses = ['connected', 'syncing', 'connected'];
      let index = 0;
      const interval = setInterval(() => {
        setConnectionStatus(statuses?.[index % statuses?.length]);
        index++;
      }, 5000);
      return interval;
    };

    const interval = simulateConnection();
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (usernameCheckTimer.current) {
        clearTimeout(usernameCheckTimer.current);
        usernameCheckTimer.current = null;
      }
    };
  }, []);

  // Open Change Username modal
  const openChangeUsername = async () => {
    setIsChangeUsernameOpen(true);
    setNewUsernameInput('');
    setUsernameValidation('');
    setUsernameAvailable(null);
    setUsernameChangeError('');
    setUsernameChangeSuccess(false);
    setUsernameCheckLoading(false);
    setUsernameChangeLoading(false);
    // Load cooldown and history
    if (user?.id) {
      const [cooldownResult, history] = await Promise.all([
        canChangeUsername(user.id),
        getUsernameHistory(user.id),
      ]);
      setUsernameCooldown(cooldownResult);
      setUsernameHistory(history);
    }
  };

  // Real-time username validation and availability check
  const handleUsernameInputChange = useCallback((value) => {
    setNewUsernameInput(value);
    setUsernameAvailable(null);
    setUsernameChangeError('');

    // Clear previous timer
    if (usernameCheckTimer.current) {
      clearTimeout(usernameCheckTimer.current);
    }

    // Validate format first
    const error = validateUsername(value);
    if (error) {
      setUsernameValidation(error);
      return;
    }
    setUsernameValidation('');

    // If same as current username, skip
    if (value.trim().toLowerCase() === (username || '').toLowerCase()) {
      setUsernameValidation('This is your current username');
      return;
    }

    // Debounced availability check
    setUsernameCheckLoading(true);
    usernameCheckTimer.current = setTimeout(async () => {
      try {
        const available = await checkUsernameAvailability(value.trim(), user?.id);
        setUsernameAvailable(available);
        if (!available) {
          setUsernameValidation('Username is already taken');
        } else {
          setUsernameValidation('');
        }
      } catch {
        setUsernameValidation('Could not check availability');
      } finally {
        setUsernameCheckLoading(false);
      }
    }, 400);
  }, [username, user?.id]);

  // Submit username change
  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    setUsernameChangeError('');

    // Check cooldown
    if (usernameCooldown && !usernameCooldown.canChange) {
      setUsernameChangeError(`You can only change your username once every ${USERNAME_COOLDOWN_DAYS} days. Next change available: ${usernameCooldown.nextChangeDate?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`);
      return;
    }

    try {
      setUsernameChangeLoading(true);
      const newName = await changeUsername(user.id, newUsernameInput.trim());
      setUsername(newName);
      setUsernameChangeSuccess(true);
      // Refresh history
      const history = await getUsernameHistory(user.id);
      setUsernameHistory(history);
      // Auto-close after 2 seconds
      setTimeout(() => {
        setIsChangeUsernameOpen(false);
        setUsernameChangeSuccess(false);
      }, 2000);
    } catch (err) {
      setUsernameChangeError(err?.message || 'Failed to change username');
    } finally {
      setUsernameChangeLoading(false);
    }
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'syncing':
        return 'Syncing...';
      case 'disconnected':
        return 'Disconnected';
      default:
        return 'Unknown';
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <header className="header-nav">
        {/* Top Row: Logo + Right Section */}
        <div className="header-container flex-col lg:flex-row">
          <div className="flex items-center justify-between w-full gap-2 lg:gap-8">
            <Link to="/main-dashboard" className="header-logo">
              <div className="header-logo-icon bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Icon name="UtensilsCrossed" size={20} color="white" />
              </div>
              <div className="hidden sm:flex flex-col">
                <span className="header-logo-text font-bold text-base bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">Meal Manager</span>
                <span className="text-xs font-semibold text-muted-foreground">Capital Care Homes</span>
              </div>
            </Link>

            <div className="header-right-section">
              {role === 'staff' && careHomeName && (
                <div className="hidden sm:flex items-center gap-2 px-2 lg:px-4 py-2 rounded-lg bg-muted/50 border border-border min-w-0">
                  <Icon name="Building2" size={16} color="var(--color-primary)" className="flex-shrink-0" />
                  <span className="text-xs lg:text-sm font-medium text-foreground truncate">{careHomeName}</span>
                </div>
              )}
              
              <div className="status-indicator hidden lg:flex" title={getStatusText()}>
                <div className={`status-dot ${connectionStatus}`}></div>
                <span className="text-xs text-muted-foreground">{getStatusText()}</span>
              </div>

              <NotificationBell />

              <div className="relative" ref={userMenuRef}>
                <button
                  className="group flex items-center gap-1 px-1.5 lg:px-3 py-2 rounded-lg transition-all duration-300 min-w-0"
                  style={{
                    background: isUserMenuOpen 
                      ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)'
                      : 'transparent',
                    transform: isUserMenuOpen ? 'translateY(-1px)' : 'translateY(0)',
                  }}
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                  title={displayName || user?.email?.split('@')?.[0] || 'User'}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:from-primary/30 group-hover:to-accent/30">
                    <Icon name="User" size={16} color="var(--color-primary)" />
                  </div>
                  <span className="hidden lg:inline text-sm font-semibold text-foreground whitespace-nowrap truncate max-w-[100px] group-hover:text-primary transition-colors duration-300">
                    {displayName || user?.email?.split('@')?.[0] || 'User'}
                  </span>
                  <Icon 
                    name={isUserMenuOpen ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                    className="flex-shrink-0 hidden lg:block transition-transform duration-300" 
                    style={{ transform: isUserMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </button>

                {isUserMenuOpen && (
                  <div 
                    className="absolute right-0 mt-2 w-56 rounded-lg border border-border py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.08)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <div className="px-4 py-3 border-b border-border/50">
                      <p className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{displayName || 'User'}</p>
                      {username && <p className="text-xs text-muted-foreground mt-0.5">@{username}</p>}
                      <p className="text-xs text-muted-foreground mt-1.5 truncate">{user?.email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setIsUserMenuOpen(false); navigate('/profile'); }}
                      className="w-full px-4 py-2 text-left text-sm font-medium text-foreground hover:text-primary transition-colors flex items-center gap-2"
                    >
                      <Icon name="User" size={16} />
                      Profile
                    </button>
                    {(isOrgAdmin || isCareHomeManager) && !isSuperAdmin ? (
                      <button
                        type="button"
                        onClick={() => { setIsUserMenuOpen(false); navigate('/organisation'); }}
                        className="w-full px-4 py-2 text-left text-sm font-medium text-foreground hover:text-primary transition-colors flex items-center gap-2"
                      >
                        <Icon name="Building2" size={16} />
                        Organisation
                      </button>
                    ) : null}
                    <button
                      onClick={() => { setIsUserMenuOpen(false); openChangeUsername(); }}
                      className="w-full px-4 py-2 text-left text-sm font-medium text-foreground hover:text-primary transition-all duration-300 flex items-center gap-2"
                      style={{
                        background: 'linear-gradient(90deg, rgba(37, 99, 235, 0) 0%, rgba(37, 99, 235, 0) 0%)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(90deg, rgba(37, 99, 235, 0.02) 0%, transparent 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Icon name="AtSign" size={16} />
                      Change Username
                    </button>
                    <button
                      onClick={() => { setIsUserMenuOpen(false); setIsChangePasswordOpen(true); }}
                      className="w-full px-4 py-2 text-left text-sm font-medium text-foreground hover:text-primary transition-all duration-300 flex items-center gap-2"
                      style={{
                        background: 'linear-gradient(90deg, rgba(37, 99, 235, 0) 0%, rgba(37, 99, 235, 0) 0%)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(90deg, rgba(37, 99, 235, 0.02) 0%, transparent 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Icon name="Key" size={16} />
                      Change Password
                    </button>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        navigate(isSuperAdmin ? '/super-admin?tab=billing' : '/billing');
                      }}
                      className="w-full px-4 py-2 text-left text-sm font-medium text-foreground hover:text-primary transition-all duration-300 flex items-center gap-2"
                      style={{
                        background: 'linear-gradient(90deg, rgba(37, 99, 235, 0) 0%, rgba(37, 99, 235, 0) 0%)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(90deg, rgba(37, 99, 235, 0.02) 0%, transparent 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Icon name="CreditCard" size={16} />
                      Billing
                    </button>

                    <button
                      onClick={() => { setIsUserMenuOpen(false); setIsSupportHistoryOpen(true); }}
                      className="w-full px-4 py-2 text-left text-sm font-medium text-foreground hover:text-primary transition-all duration-300 flex items-center gap-2"
                      style={{
                        background: 'linear-gradient(90deg, rgba(37, 99, 235, 0) 0%, rgba(37, 99, 235, 0) 0%)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(90deg, rgba(37, 99, 235, 0.02) 0%, transparent 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Icon name="MessageCircle" size={16} />
                      Support History
                    </button>

                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm font-medium text-foreground hover:text-primary transition-all duration-300 flex items-center gap-2 group/logout"
                      style={{
                        background: 'linear-gradient(90deg, rgba(37, 99, 235, 0) 0%, rgba(37, 99, 235, 0) 0%)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, transparent 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Icon name="LogOut" size={16} className="group-hover/logout:translate-x-0.5 transition-transform" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>

            {/* Change Password Modal (render via portal so it escapes header stacking context) */}
        {isChangePasswordOpen && createPortal(
          <div className="fixed inset-0 bg-black/60 z-[20000] flex items-center justify-center p-4" onClick={() => { if (!changeSuccess) setIsChangePasswordOpen(false); }}>
            <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Change Password</h3>
                <button onClick={() => { if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; } setIsChangePasswordOpen(false); setChangeSuccess(false); setSuccessCountdown(0); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700"><Icon name="X" size={18} /></button>
              </div>

              {/* Animated success overlay inside modal */}
              {changeSuccess && (
                <div className="absolute inset-0 flex items-center justify-center z-[20010] pointer-events-auto">
                  {/* backdrop to give contrast and reduce perceived transparency (non-interactive) */}
                  <div className="absolute inset-0 bg-black/30 rounded-2xl pointer-events-none" />

                  <div className="animate-in fade-in slide-in-from-top-2 bg-white dark:bg-slate-900 rounded-xl p-6 flex flex-col items-center gap-3 shadow-2xl ring-1 ring-slate-100 dark:ring-slate-800 max-w-xs pointer-events-auto">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
                      <Icon name="CheckCircle2" size={28} />
                    </div>
                    <div className="text-lg font-bold text-slate-900 dark:text-white">Password updated</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Your password has been changed successfully.</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Closing in {successCountdown}s</div>
                  </div>
                </div>
              )} 

              {changeError && <div className="p-3 bg-red-50 border border-red-200 rounded mb-3 text-sm text-red-700">{changeError}</div>}

              <form onSubmit={async (e) => {
                e.preventDefault();
                setChangeError('');
                setChangeSuccess(false);
                if (!currentPassword) return setChangeError('Enter your current password');
                if (!newPassword || newPassword.length < 8) return setChangeError('New password must be at least 8 characters');
                if (newPassword !== confirmPassword) return setChangeError('Passwords do not match');
                try {
                  setChangeLoading(true);
                  // Verify current password
                  const email = user?.email;
                  if (!email) throw new Error('Cannot verify user email');
                  const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({ email, password: currentPassword });
                  if (signInError) throw signInError;
                  // Update password
                  const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
                  if (updateError) throw updateError;
                  setChangeSuccess(true);
                  // start a 3-second countdown and close when it reaches 0
                  setSuccessCountdown(3);
                  if (countdownRef.current) {
                    clearInterval(countdownRef.current);
                    countdownRef.current = null;
                  }
                  countdownRef.current = setInterval(() => {
                    setSuccessCountdown((prev) => {
                      if (prev <= 1) {
                        if (countdownRef.current) {
                          clearInterval(countdownRef.current);
                          countdownRef.current = null;
                        }
                        setIsChangePasswordOpen(false);
                        setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setChangeSuccess(false);
                        return 0;
                      }
                      return prev - 1;
                    });
                  }, 1000);
                } catch (err) {
                  setChangeError(err?.message || 'Failed to change password');
                } finally {
                  setChangeLoading(false);
                }
              }}>
                <div className="space-y-3">
                  <div className="relative">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Current password</label>
                    <input type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} disabled={changeLoading || changeSuccess} className="w-full pr-10 px-3 py-2 rounded-lg border-2 border-slate-200 bg-white dark:bg-slate-700" />
                    <button type="button" onClick={() => setShowCurrentPassword(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700">
                      <Icon name={showCurrentPassword ? 'EyeOff' : 'Eye'} size={16} />
                    </button>
                  </div>

                  <div className="relative">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">New password</label>
                    <input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={changeLoading || changeSuccess} className="w-full pr-10 px-3 py-2 rounded-lg border-2 border-slate-200 bg-white dark:bg-slate-700" />
                    <button type="button" onClick={() => setShowNewPassword(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700">
                      <Icon name={showNewPassword ? 'EyeOff' : 'Eye'} size={16} />
                    </button>
                    <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
                  </div>

                  <div className="relative">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Confirm new password</label>
                    <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={changeLoading || changeSuccess} className="w-full pr-10 px-3 py-2 rounded-lg border-2 border-slate-200 bg-white dark:bg-slate-700" />
                    <button type="button" onClick={() => setShowConfirmPassword(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700">
                      <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={16} />
                    </button>
                  </div>

                  <div className="flex justify-end gap-3 pt-3">
                    <button type="button" onClick={() => { if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; } setIsChangePasswordOpen(false); setChangeSuccess(false); setSuccessCountdown(0); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} disabled={changeLoading} className="px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700">Cancel</button>
                    <button type="submit" disabled={changeLoading || changeSuccess} className={`px-4 py-2 rounded-lg text-white ${changeLoading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
                      {changeLoading ? 'Updating...' : 'Update password'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        <SupportHistoryModal
          isOpen={isSupportHistoryOpen}
          onClose={() => setIsSupportHistoryOpen(false)}
        />

        {/* Change Username Modal */}
        {isChangeUsernameOpen && createPortal(
          <div className="fixed inset-0 bg-black/60 z-[20000] flex items-center justify-center p-4" onClick={() => { if (!usernameChangeSuccess) setIsChangeUsernameOpen(false); }}>
            <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 pb-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Icon name="AtSign" size={20} className="text-blue-500" />
                  Change Username
                </h3>
                <button onClick={() => { setIsChangeUsernameOpen(false); setUsernameChangeSuccess(false); }} className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700">
                  <Icon name="X" size={18} />
                </button>
              </div>

              {/* Success overlay */}
              {usernameChangeSuccess && (
                <div className="absolute inset-0 flex items-center justify-center z-[20010] pointer-events-auto">
                  <div className="absolute inset-0 bg-black/30 rounded-2xl pointer-events-none" />
                  <div className="animate-in fade-in slide-in-from-top-2 bg-white dark:bg-slate-900 rounded-xl p-6 flex flex-col items-center gap-3 shadow-2xl ring-1 ring-slate-100 dark:ring-slate-800 max-w-xs pointer-events-auto">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
                      <Icon name="CheckCircle2" size={28} />
                    </div>
                    <div className="text-lg font-bold text-slate-900 dark:text-white">Username updated!</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Your new username is <span className="font-bold text-blue-600">@{newUsernameInput.trim()}</span></div>
                  </div>
                </div>
              )}

              <div className="px-6 pb-6 space-y-4">
                {/* Current username */}
                <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Current Username</p>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">{username ? `@${username}` : 'No username set'}</p>
                </div>

                {/* Cooldown notice */}
                {usernameCooldown && !usernameCooldown.canChange && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={16} className="text-amber-500" />
                      <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">Username change on cooldown</p>
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      You can change your username again on {usernameCooldown.nextChangeDate?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} ({usernameCooldown.daysRemaining} day{usernameCooldown.daysRemaining !== 1 ? 's' : ''} remaining)
                    </p>
                  </div>
                )}

                {/* New username input */}
                {(!usernameCooldown || usernameCooldown.canChange) && (
                  <form onSubmit={handleUsernameSubmit}>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">New Username</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
                          <input
                            type="text"
                            value={newUsernameInput}
                            onChange={(e) => handleUsernameInputChange(e.target.value)}
                            placeholder="username"
                            disabled={usernameChangeLoading || usernameChangeSuccess}
                            className="w-full pl-8 pr-10 px-3 py-2 rounded-lg border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-blue-400 transition-colors"
                            maxLength={30}
                            autoFocus
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {usernameCheckLoading && (
                              <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                            )}
                            {!usernameCheckLoading && usernameAvailable === true && !usernameValidation && (
                              <Icon name="CheckCircle2" size={16} className="text-emerald-500" />
                            )}
                            {!usernameCheckLoading && usernameAvailable === false && (
                              <Icon name="XCircle" size={16} className="text-red-500" />
                            )}
                          </div>
                        </div>
                        {usernameValidation && (
                          <p className="text-xs text-red-500 mt-1">{usernameValidation}</p>
                        )}
                        {!usernameValidation && usernameAvailable === true && (
                          <p className="text-xs text-emerald-500 mt-1">Username is available!</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">3-30 characters. Letters, numbers, dots, hyphens, underscores.</p>
                      </div>

                      {usernameChangeError && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">{usernameChangeError}</div>
                      )}

                      <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={() => setIsChangeUsernameOpen(false)} disabled={usernameChangeLoading} className="px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-sm font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">Cancel</button>
                        <button
                          type="submit"
                          disabled={usernameChangeLoading || usernameChangeSuccess || !usernameAvailable || !!usernameValidation || !newUsernameInput.trim()}
                          className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${usernameChangeLoading || !usernameAvailable || !!usernameValidation || !newUsernameInput.trim() ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                        >
                          {usernameChangeLoading ? 'Updating...' : 'Update Username'}
                        </button>
                      </div>
                    </div>
                  </form>
                )}

                {/* Username history */}
                {usernameHistory.length > 0 && (
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1.5">
                      <Icon name="History" size={14} />
                      Username History
                    </p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {usernameHistory.map((entry, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-slate-50 dark:bg-slate-700/30">
                          <span className="text-slate-400 dark:text-slate-500 line-through">{entry.old_username || 'none'}</span>
                          <Icon name="ArrowRight" size={12} className="text-slate-400" />
                          <span className="font-semibold text-slate-700 dark:text-slate-300">{entry.new_username}</span>
                          <span className="ml-auto text-slate-400">
                            {new Date(entry.changed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Info note */}
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <p className="text-xs text-blue-600 dark:text-blue-400 flex items-start gap-1.5">
                    <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
                    <span>Your username is your public identity shown on community posts and shared meals. You can change it once every {USERNAME_COOLDOWN_DAYS} days.</span>
                  </p>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

          <button
                className="lg:hidden p-2 rounded-lg transition-all duration-300 flex-shrink-0 group"
                style={{
                  background: isMobileMenuOpen 
                    ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)'
                    : 'transparent',
                }}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle mobile menu"
                aria-expanded={isMobileMenuOpen}
              >
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} className="group-hover:text-primary transition-colors duration-300" />
              </button>
            </div>
          </div>

          {/* Bottom Row: Navigation (Care Home Manager and Staff on tablets and mobile) */}
          {(role === 'care_home_manager' || role === 'staff') && (
            <nav className="header-nav-items flex lg:hidden w-full border-t border-border pt-2 mt-2 overflow-x-auto no-scrollbar">
              {(isCareHomeManagerRole ? careHomeManagerNavItems : staffNavItems)?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`header-nav-link flex-shrink-0 ${isActivePath(item?.path) ? 'active' : ''}`}
                  title={item?.label}
                >
                  <span className="flex items-center gap-1">
                    <Icon name={item?.icon} size={14} />
                    <span>{item?.label}</span>
                  </span>
                </Link>
              ))}
            </nav>
          )}
        </div>

        {/* Desktop Navigation (Hidden on tablets and mobile) */}
        <nav className="header-nav-items hidden lg:flex px-4 lg:px-6 border-t border-border">
          {isAdminLikeRole ? (
            <>
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`header-nav-link ${isActivePath(item?.path) ? 'active' : ''}`}
                  title={item?.label}
                >
                  <span className="flex items-center gap-1">
                    <Icon name={item?.icon} size={14} />
                    <span>{item?.label}</span>
                  </span>
                </Link>
              ))}
              <div className="w-px h-6 bg-border mx-0.5"></div>
              {adminNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`header-nav-link ${isActivePath(item?.path) ? 'active' : ''}`}
                  title={item?.label}
                >
                  <span className="flex items-center gap-1">
                    <Icon name={item?.icon} size={14} />
                    <span>{item?.label}</span>
                  </span>
                </Link>
              ))}
              {/* Super Admin links - only visible to Super Admin users */}
              {isSuperAdmin && (
                <>
                  <div className="w-px h-6 bg-border mx-0.5"></div>
                  {superAdminNavItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`header-nav-link ${isActivePath(item.path) ? 'active' : ''}`}
                      title={item.label}
                    >
                      <span className="flex items-center gap-1">
                        <Icon name={item.icon} size={14} className="text-primary" />
                        <span className="font-semibold">{item.label}</span>
                      </span>
                    </Link>
                  ))}
                </>
              )}
            </>
          ) : isCareHomeManagerRole ? (
            <>
              {careHomeManagerNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`header-nav-link ${isActivePath(item?.path) ? 'active' : ''}`}
                  title={item?.label}
                >
                  <span className="flex items-center gap-1">
                    <Icon name={item?.icon} size={14} />
                    <span>{item?.label}</span>
                  </span>
                </Link>
              ))}
              {careHomeManagerAdminItems.length > 0 && (
                <>
                  <div className="w-px h-6 bg-border mx-0.5"></div>
                  {careHomeManagerAdminItems?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      className={`header-nav-link ${isActivePath(item?.path) ? 'active' : ''}`}
                      title={item?.label}
                    >
                      <span className="flex items-center gap-1">
                        <Icon name={item?.icon} size={14} />
                        <span>{item?.label}</span>
                      </span>
                    </Link>
                  ))}
                </>
              )}
            </>
          ) : (
            staffNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`header-nav-link ${isActivePath(item?.path) ? 'active' : ''}`}
                title={item?.label}
              >
                <span className="flex items-center gap-1">
                  <Icon name={item?.icon} size={14} />
                  <span>{item?.label}</span>
                </span>
              </Link>
            ))
          )}
        </nav>
      </header>
      {isMobileMenuOpen && (
        <>
          <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)}></div>
          <div className="mobile-menu">
            <div className="mobile-menu-header">
              <div className="header-logo">
                <div className="header-logo-icon">
                  <Icon name="Utensils" size={24} color="white" />
                </div>
                <span className="header-logo-text">CareHome Food</span>
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
                <Icon name="X" size={24} />
              </button>
            </div>

            <nav className="mobile-menu-nav">
              {mobilePrimaryNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`mobile-menu-link ${isActivePath(item?.path) ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="flex items-center gap-3">
                    <Icon name={item?.icon} size={20} />
                    {item?.label}
                  </span>
                </Link>
              ))}

              {(isAdminLikeRole || isCareHomeManagerRole) && (
                <>
                  <div className="border-t border-border my-4 pt-4">
                    <p className="px-4 text-xs font-semibold text-muted-foreground mb-2">ADMINISTRATION</p>
                    {(isCareHomeManagerRole ? careHomeManagerAdminItems : adminNavItems)?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className={`mobile-menu-link ${isActivePath(item?.path) ? 'active' : ''}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="flex items-center gap-3">
                          <Icon name={item?.icon} size={20} />
                          {item?.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {/* Super Admin Section - Only for Super Admin users */}
              {isSuperAdmin && (
                <div className="border-t border-border my-4 pt-4">
                  <p className="px-4 text-xs font-semibold text-primary mb-2">SUPER ADMIN</p>
                  {superAdminNavItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`mobile-menu-link ${isActivePath(item.path) ? 'active' : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="flex items-center gap-3">
                        <Icon name={item.icon} size={20} className="text-primary" />
                        <span className="font-semibold">{item.label}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-border">
                <div className="px-4 mt-4">
                  <div className="text-xs font-medium text-muted-foreground mb-2">Connection Status</div>
                  <div className="flex items-center gap-2">
                    <div className={`status-dot ${connectionStatus}`}></div>
                    <span className="text-sm text-foreground">{getStatusText()}</span>
                  </div>
                </div>

                <div className="px-4 mt-4 space-y-2">
                  <button
                    type="button"
                    onClick={() => { setIsMobileMenuOpen(false); navigate('/profile'); }}
                    className="w-full px-4 py-2.5 text-left text-sm font-medium text-foreground rounded-lg border border-border hover:text-primary flex items-center gap-2"
                  >
                    <Icon name="User" size={16} />
                    Profile
                  </button>
                  {(isOrgAdmin || isCareHomeManager) && !isSuperAdmin ? (
                    <button
                      type="button"
                      onClick={() => { setIsMobileMenuOpen(false); navigate('/organisation'); }}
                      className="w-full px-4 py-2.5 text-left text-sm font-medium text-foreground rounded-lg border border-border hover:text-primary flex items-center gap-2"
                    >
                      <Icon name="Building2" size={16} />
                      Organisation
                    </button>
                  ) : null}
                  <button
                    onClick={() => { setIsChangePasswordOpen(true); setIsMobileMenuOpen(false); }}
                    className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 mb-2"
                  >
                    <Icon name="Key" size={16} />
                    Change Password
                  </button>

                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate(isSuperAdmin ? '/super-admin?tab=billing' : '/billing');
                    }}
                    className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 mb-2"
                  >
                    <Icon name="CreditCard" size={16} />
                    Billing
                  </button>

                  <button
                    onClick={() => { setIsSupportHistoryOpen(true); setIsMobileMenuOpen(false); }}
                    className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 mb-2"
                  >
                    <Icon name="MessageCircle" size={16} />
                    Support History
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 bg-error/10 text-error rounded-lg hover:bg-error/20 transition-colors flex items-center justify-center gap-2"
                  >
                    <Icon name="LogOut" size={16} />
                    Sign Out
                  </button>
                </div>
              </div>
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default Header;